import 'react-h5-audio-player/lib/styles.css';

import { useEffect, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, styled, useTheme } from '@mui/material';
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from '@tabler/icons-react';
import { IconDownload } from '@tabler/icons-react';

import {
  Alert,
  IconButton,
  LoadingIndicator,
} from '@zendesk/forethought-elements';

export const RecordingPlayer = ({
  alertMessage = '',
  audioBlob,
  fileName,
  isLoading,
}: {
  alertMessage?: string;
  audioBlob?: Blob | null;
  fileName: string;
  isLoading: boolean;
}) => {
  const [isAlertVisible, setIsAlertVisible] = useState(true);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          pb: 2,
        }}
      >
        <Skeleton height='20px' variant='rounded' width='100%' />
        <Skeleton height='50px' variant='rounded' width='100%' />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        pb: isAlertVisible ? 2 : 0,
      }}
    >
      {isAlertVisible && alertMessage && (
        <Alert
          isFullWidth={true}
          onClose={() => {
            setIsAlertVisible(false);
          }}
          title={alertMessage}
          variant='default'
        />
      )}
      {audioBlob && <BlobAudioPlayer blob={audioBlob} fileName={fileName} />}
    </Box>
  );
};

export const BlobAudioPlayer = ({
  blob,
  fileName,
}: {
  blob: Blob;
  fileName: string;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(blob);
    setAudioUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  const handleDownload = () => {
    if (!audioUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = audioUrl;

    link.download = `${fileName}.wav`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!audioUrl) {
    return (
      <LoadingIndicator color={palette.colors.primary[500]} size='large' />
    );
  }

  return (
    <AudioPlayerWrapper>
      <AudioPlayer
        autoPlay={false}
        customAdditionalControls={[
          <IconButton
            aria-label={t('recordingPlayer.downloadAudio')}
            key='download'
            onClick={handleDownload}
            variant='ghost'
          >
            <IconDownload size={20} />
          </IconButton>,
        ]}
        customIcons={{
          pause: (
            <PlayButton>
              <IconPlayerPauseFilled
                color={palette.colors.primary[500]}
                size={25}
              />
            </PlayButton>
          ),
          play: (
            <PlayButton>
              <IconPlayerPlayFilled
                color={palette.colors.primary[500]}
                size={25}
              />
            </PlayButton>
          ),
        }}
        showDownloadProgress={false}
        showFilledVolume={true}
        showJumpControls={false}
        src={audioUrl}
      />
    </AudioPlayerWrapper>
  );
};

const AudioPlayerWrapper = styled('div')`
  .rhap_container {
    background-color: rgba(242, 244, 247, 0.5);

    border: 1px solid ${props => props.theme.palette.colors.grey[100]};
    border-radius: 5px;
    padding: 16px;
    box-shadow: none;
  }

  .rhap_time {
    font-size: 14px;
    color: ${props => props.theme.palette.colors.grey[700]};
  }

  .rhap_play-pause-button {
    color: ${props => props.theme.palette.colors.primary[500]};
  }

  .rhap_progress-bar {
    background-color: ${props => props.theme.palette.colors.grey[100]};
  }

  .rhap_volume-bar {
    background-color: ${props => props.theme.palette.colors.grey[100]};
  }

  .rhap_progress-filled {
    background-color: ${props => props.theme.palette.colors.grey[700]};
  }
  .rhap_volume-filled {
    background-color: ${props => props.theme.palette.colors.grey[700]};
  }

  .rhap_repeat-button {
    display: None;
  }

  .rhap_progress-indicator {
    display: None;
  }

  .rhap_volume-indicator {
    display: None;
  }
`;

const PlayButton = styled('div')`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.colors.primary[300]};
  background-color: ${props => props.theme.palette.colors.primary[100]};
  display: flex;
  align-items: center;
  justify-content: center;
`;
